import React from 'react';
import usePreviousDistinct from 'react-use/lib/usePreviousDistinct';
import {Button, Controls, ControlsItem, Notification, Typography} from '@genestack/ui';
import {LS_PREFIX} from '../../../../../providers/global-state';
import {
    GraphNode,
    GraphViewKind,
    GraphEdge,
    QueryGraphLayoutAlgorithm
} from '../../../../../components/graph/interface';
import {useLsValue} from '../../../../../hooks/use-ls-value';
import {defaultPan, defaultZoom, useCameraProps} from './use-camera-props';
import {useCalculateLayout} from '../../../../../components/graph/graph-logic/use-calculate-layout';
import {
    GraphPresentation,
    GraphPresentationDetails,
    QueryGraphAction,
    QueryGraphEffect
} from './interface';
import {persistPositionsByRawNodeId} from './helpers';
import {
    NotificationElement,
    showNotification
} from '../../../../../providers/notifications-center/show-notification';
import {useNodePositionsState} from './use-node-positions-state';
import {defaultPresentationId} from './use-graph-presentation-props';

interface Props {
    maxPathLength: number;
    viewKind: GraphViewKind;
    queryId: number;
    nodes?: GraphNode[];
    edges?: GraphEdge[];
    selectedPresentation: GraphPresentation;
    selectedPresentationDetails: GraphPresentationDetails;
    handlePresentationEdited: () => void;
    isPresentationEdited: boolean;
    isHiddenNodesVisible: boolean;
    isHiddenNode: (nodeId: number) => boolean;
    isHiddenEdge: (edgeId: number) => boolean;
    addToHistory: (actionsOrEffects: Array<QueryGraphAction | QueryGraphEffect>) => void;
}

interface SavedLayoutNotificationProps {
    onClick?: () => void;
    onClose?: () => void;
    isDestroyed?: boolean;
}

function getSavedLayoutNotification(props: SavedLayoutNotificationProps) {
    return (
        <Notification
            countdown={props.isDestroyed ? 'active' : 'none'}
            countdownDuration={0}
            onClose={props.onClose}
        >
            <Typography intent="warning">The previous graph layout was left unchanged</Typography>
            <Controls justify="end">
                <ControlsItem>
                    <Button size="small" onClick={props.onClick}>
                        Rebuild layout
                    </Button>
                </ControlsItem>
            </Controls>
        </Notification>
    ) as NotificationElement;
}

// todo: separate layout and camera props
export function useLayoutAndCameraProps(props: Props) {
    const {
        queryId,
        maxPathLength,
        viewKind,
        nodes,
        edges,
        selectedPresentation,
        selectedPresentationDetails,
        handlePresentationEdited,
        isPresentationEdited,
        isHiddenNodesVisible,
        isHiddenNode,
        isHiddenEdge,
        addToHistory
    } = props;

    const addToHistoryRef = React.useRef(addToHistory);
    addToHistoryRef.current = addToHistory;

    const [isBrowserUnsupported, setIsBrowserUnsupported] = React.useState<boolean>();

    const [layoutAlgorithm, setLayoutAlgorithm, clearLayoutAlgorithm] = useLsValue<
        QueryGraphLayoutAlgorithm,
        [number, number],
        []
    >({
        changeEntityParams: [queryId, selectedPresentation.id],
        getKey: ([qId, pId]) => `${LS_PREFIX}.queryGraph.${qId}.presentation${pId}.layoutAlgorithm`,
        rewriteKeyParams: [],
        defaultValue: selectedPresentationDetails.layoutAlgorithm,
        validateValue: (value) =>
            Object.values(QueryGraphLayoutAlgorithm).includes(value as QueryGraphLayoutAlgorithm)
    });
    const layoutAlgorithmRef = React.useRef(layoutAlgorithm);
    layoutAlgorithmRef.current = layoutAlgorithm;

    const {zoom, pan, setZoom, setPan, clearZoom, clearPan, ...restCameraProps} = useCameraProps({
        queryId,
        layoutAlgorithm,
        maxPathLength,
        viewKind,
        selectedPresentation,
        selectedPresentationDetails
    });

    const [isCustomLayout, setIsCustomLayout, clearIsCustomLayout] = useLsValue<
        'true' | 'false',
        [number, number],
        [number, GraphViewKind, QueryGraphLayoutAlgorithm]
    >({
        changeEntityParams: [queryId, selectedPresentation.id],
        rewriteKeyParams: [maxPathLength, viewKind, layoutAlgorithm],
        getKey: ([qId, pId], [mPLength, vKind, lAlgo]) =>
            `${LS_PREFIX}.queryGraph.${qId}.presentation${pId}.${mPLength}.${vKind}.${lAlgo}.isCustomLayout`,
        defaultValue: isPresentationEdited
            ? 'false'
            : (String(selectedPresentationDetails.isLayoutCustom) as 'true' | 'false'),
        validateValue: (value) => value === 'true' || value === 'false'
    });
    const isCustomLayoutRef = React.useRef(isCustomLayout === 'true');
    isCustomLayoutRef.current = isCustomLayout === 'true';

    const {
        visibleNodesPositions,
        setVisibleNodesPositions,
        resetNodePositions,
        clearVisibleNodesPositions,
        invisibleNodesPositions,
        setInvisibleNodesPositions,
        prevVisibleNodesPositions,
        prevInvisibleNodesPositions
    } = useNodePositionsState({
        queryId,
        selectedPresentation,
        selectedPresentationDetails,
        isHiddenNodesVisible,
        isHiddenNode
    });
    const prevVisibleNodePositionsRef = React.useRef(prevVisibleNodesPositions);
    prevVisibleNodePositionsRef.current = prevVisibleNodesPositions;
    const visibleNodePositionsRef = React.useRef(visibleNodesPositions);
    visibleNodePositionsRef.current = visibleNodesPositions;
    const prevInvisibleNodePositionsRef = React.useRef(prevInvisibleNodesPositions);
    prevInvisibleNodePositionsRef.current = prevInvisibleNodesPositions;
    const setVisibleNodePositionsRef = React.useRef(setVisibleNodesPositions);
    setVisibleNodePositionsRef.current = setVisibleNodesPositions;

    const onLayoutCalculationStarted = React.useCallback(() => {
        addToHistoryRef.current([
            {
                effectType: 'LAYOUT_CALCULATION_STARTED',
                previousLayout: prevVisibleNodePositionsRef.current
                    ? JSON.parse(prevVisibleNodePositionsRef.current)
                    : undefined
            }
        ]);
    }, []);

    const onLayoutCalculated = React.useCallback(
        (pos: string) => {
            setVisibleNodePositionsRef.current(pos);
            clearPan();
            clearZoom();
            addToHistoryRef.current([
                {
                    effectType: 'LAYOUT_CALCULATED',
                    newLayout: JSON.parse(pos)
                }
            ]);
        },
        [clearZoom, clearPan]
    );

    const {calculateLayout, terminateLayoutCalculation} = useCalculateLayout({
        nodes,
        edges,
        onCalculated: onLayoutCalculated,
        onCalculationStarted: onLayoutCalculationStarted,
        setIsBrowserUnsupported,
        layoutAlgorithm,
        isHiddenNodesVisible,
        isHiddenEdge,
        isHiddenNode
    });

    const previousLoadedNodes = usePreviousDistinct(
        nodes,
        (prev, next) => !next || JSON.stringify(prev) === JSON.stringify(next)
    );

    const previousLoadedNodesRef = React.useRef(previousLoadedNodes);
    previousLoadedNodesRef.current = previousLoadedNodes;
    const isShowingNotificationRef = React.useRef(false);
    const terminateLayoutCalculationRef = React.useRef(terminateLayoutCalculation);
    terminateLayoutCalculationRef.current = terminateLayoutCalculation;

    const onNodesLoaded = React.useCallback(
        (newNodes: GraphNode[], prevNodes?: GraphNode[]) => {
            terminateLayoutCalculationRef.current();

            if (visibleNodePositionsRef.current !== undefined) {
                return;
            }

            if (prevNodes && prevInvisibleNodePositionsRef.current) {
                const newInvisibleNodesPositions = persistPositionsByRawNodeId(
                    prevNodes,
                    newNodes,
                    prevInvisibleNodePositionsRef.current
                );
                setInvisibleNodesPositions(JSON.stringify(newInvisibleNodesPositions));
            }

            if (
                prevVisibleNodePositionsRef.current &&
                prevNodes &&
                prevNodes.length > newNodes.length
            ) {
                // if new nodes arr has fewer nodes then previous nodes arr,
                // then try to save the layout using rawNodeIds...
                const newPositions = persistPositionsByRawNodeId(
                    prevNodes,
                    newNodes,
                    prevVisibleNodePositionsRef.current
                );

                setVisibleNodesPositions(JSON.stringify(newPositions));

                // eslint-disable-next-line prefer-const
                let closeNotificationFun: () => void;
                const notification = getSavedLayoutNotification({
                    onClick: () => {
                        setVisibleNodesPositions('');
                        calculateLayout();
                        closeNotificationFun!();
                        setPan(defaultPan);
                        setZoom(Number(defaultZoom));
                        addToHistoryRef.current([
                            {
                                actionType: 'REBUILD_LAYOUT',
                                oldAlgorithm: layoutAlgorithmRef.current,
                                newAlgorithm: layoutAlgorithmRef.current,
                                oldLayoutWasCustom: isCustomLayoutRef.current,
                                isEditingPresentationStarted: false,
                                zoom,
                                pan
                            }
                        ]);
                    },
                    onClose: () => {
                        isShowingNotificationRef.current = false;
                    }
                });

                const removeNotification = showNotification(notification);
                isShowingNotificationRef.current = true;
                closeNotificationFun = () => {
                    if (isShowingNotificationRef.current) {
                        isShowingNotificationRef.current = false;
                        removeNotification();
                    }
                };

                return closeNotificationFun;
            }

            // ...else just recalculate layout
            calculateLayout();
        },
        [
            setVisibleNodesPositions,
            setInvisibleNodesPositions,
            setZoom,
            setPan,
            calculateLayout,
            zoom,
            pan
        ]
    );

    const isDefaultPresentation = selectedPresentation.id === defaultPresentationId;
    React.useEffect(() => {
        if (nodes?.length && !visibleNodesPositions && isDefaultPresentation) {
            calculateLayout();
        }
    }, [visibleNodesPositions, nodes, calculateLayout, isDefaultPresentation]);

    return {
        layoutAlgorithm: layoutAlgorithm as QueryGraphLayoutAlgorithm,
        clearLayoutAlgorithm,
        visibleNodesPositions,
        invisibleNodesPositions,
        clearVisibleNodesPositions,
        resetNodePositions,
        isCustomLayout: isCustomLayout === 'true',
        clearIsCustomLayout,
        setLayoutAlgorithm,
        calculateLayout,
        handlePresentationEdited,
        isBrowserUnsupported,
        setZoom,
        setPan,
        setVisibleNodesPositions,
        setInvisibleNodesPositions,
        setIsCustomLayout,
        onNodesLoaded,
        zoom,
        pan,
        ...restCameraProps
    };
}
